import i18next from "config/i18next";
import * as yup from "yup";
import "../../i18n";
import { MAX_FILE_SIZE, VALIDATION_REGEX } from "utils/constants";
import { RequiredStringSchema } from "yup/lib/string";
import { CountryCodeType } from "reducers/types/session";
import { AnyObject } from "yup/lib/types";

const validFormatError = i18next.t("validations:enterValidFormat");
const requiredError = i18next.t("validations:required");

const fileFormatSizeError = i18next.t("detailTransaction:tooltipFile");

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png"];

function getFileFromValue(value: unknown): File | null {
  if (!value) return null;
  const ev = value as { target?: { files?: FileList } };
  if (ev?.target?.files?.length) return ev.target.files[0] ?? null;
  if (value instanceof File) return value;
  if (value instanceof FileList && value.length > 0) return value[0];
  return null;
}

function isValidProofFile(file: File): boolean {
  return (
    file.size <= MAX_FILE_SIZE &&
    ALLOWED_FILE_TYPES.includes(file.type)
  );
}

export const photoRequiredError = i18next.t("detailTransaction:photoRequiredError");
export const signRequiredError = i18next.t("detailTransaction:signRequiredError");
export const photoMaxSizeError = i18next.t("detailTransaction:photoMaxSizeError");
export const signMaxSizeError = i18next.t("detailTransaction:signMaxSizeError");

const dniValidations: {
  [key in CountryCodeType]: RequiredStringSchema<string | undefined, AnyObject>;
} = {
  AR: yup
    .string()
    .min(7, i18next.t("detailTransaction:dniLongError"))
    .required(requiredError)
    .matches(VALIDATION_REGEX.regDNI, validFormatError),
  CO: yup
    .string()
    .min(6, i18next.t("detailTransaction:idLongError"))
    .required(requiredError)
    .matches(VALIDATION_REGEX.lettersAndNumbers, validFormatError),
  MX: yup
    .string()
    .required(requiredError)
    .matches(VALIDATION_REGEX.regCURP, validFormatError),
  CL: yup
    .string()
    .required(requiredError)
    .matches(VALIDATION_REGEX.regRUN, validFormatError),
};

const serialNumberCodeMismatchError = i18next.t("detailTransaction:serialNumberCodeMismatch");
const serialNumberSelectAllError = i18next.t("detailTransaction:serialNumberSelectAll");

type ValidationSchemaOptions = {
  requiresSerialNumber?: boolean;
  requiresPhoto?: boolean;
  requiresSign?: boolean;
  requiresName?: boolean;
  requiresLastName?: boolean;
  requiresIdentificationNumber?: boolean;
  requiresRelationship?: boolean;
  expectedSerialCode?: string | string[] | null;
};

const nameSchema = () =>
  yup
    .string()
    .required(i18next.t("validations:required"))
    .matches(
      VALIDATION_REGEX.expName,
      i18next.t("detailTransaction:invalidFormat")
    );

export const validationSchema = (
  countryCode: CountryCodeType,
  options?: ValidationSchemaOptions
) => {
  const {
    requiresSerialNumber = false,
    requiresPhoto = false,
    requiresSign = false,
    requiresName = false,
    requiresLastName = false,
    requiresIdentificationNumber = false,
    requiresRelationship = false,
    expectedSerialCode = null,
  } = options ?? {};
  const expectedList: string[] = Array.isArray(expectedSerialCode)
    ? expectedSerialCode
        .filter((c): c is string => typeof c === "string")
        .map((c) => c.trim())
        .filter(Boolean)
    : expectedSerialCode != null && String(expectedSerialCode).trim() !== ""
      ? [String(expectedSerialCode).trim()]
      : [];
  const hasExpectedCode = expectedList.length > 0;
  return yup.object({
    ...(requiresIdentificationNumber && {
      identificationNumber: dniValidations[countryCode],
    }),
    ...(!requiresIdentificationNumber && {
      identificationNumber: yup.string().optional(),
    }),
    ...(requiresName && { name: nameSchema() }),
    ...(!requiresName && { name: yup.string().optional() }),
    ...(requiresLastName && { lastName: nameSchema() }),
    ...(!requiresLastName && { lastName: yup.string().optional() }),
    ...(requiresRelationship && {
      relationship: yup.string().required(i18next.t("validations:required")),
    }),
    ...(!requiresRelationship && {
      relationship: yup.string().optional(),
    }),
    ...(requiresPhoto && {
      photo: yup
        .mixed()
        .required(i18next.t("validations:required"))
        .test(
          "file-format-size",
          fileFormatSizeError,
          (value) => {
            const file = getFileFromValue(value);
            if (!file) return true;
            return isValidProofFile(file);
          }
        ),
    }),
    ...(requiresSign && {
      sign: yup
        .mixed()
        .required(i18next.t("validations:required"))
        .test(
          "file-format-size",
          fileFormatSizeError,
          (value) => {
            const file = getFileFromValue(value);
            if (!file) return true;
            return isValidProofFile(file);
          }
        ),
    }),
    ...(requiresSerialNumber && {
      serialNumber: hasExpectedCode
        ? yup
            .string()
            .trim()
            .required(i18next.t("validations:required"))
            .test(
              "matches-code",
              serialNumberCodeMismatchError,
              (value) => {
                const raw = value != null ? String(value).trim() : "";
                if (raw === "") return true;
                const selected = raw.split(",").map((s) => s.trim()).filter(Boolean);
                return selected.every((s) => expectedList.includes(s));
              }
            )
            .test(
              "all-options-selected",
              serialNumberSelectAllError,
              (value) => {
                const raw = value != null ? String(value).trim() : "";
                if (raw === "" || expectedList.length === 0) return true;
                const selected = raw.split(",").map((s) => s.trim()).filter(Boolean);
                return selected.length === expectedList.length && selected.every((s) => expectedList.includes(s));
              }
            )
        : yup
            .string()
            .trim()
            .required(i18next.t("validations:required")),
    }),
  });
};
